import "../../styles/global.css";
import "./Individual.css"; // custom styles for this page
import React, { useState, useEffect, useMemo } from "react";
import {
  fetchQuestionAverageByStudent,
  fetchDemoQuestionAverageByStudent,
  fetchClickSequenceByStudent,
  fetchQuizCompletionByStudent,
  fetchLatestClickByStudent,
  fetchCurrentScoresByStudent,
  fetchMissingAssignmentsByStudent,
  fetchQuestionAverageByQuizByStudent, 
  fetchCourseLab,
} from "../../api/individualApi";
import {
  fetchQuestionAverage,
  fetchDemoQuestionAverage,
  fetchAllSubmissionDates,
  fetchAllDemoSubmissionDates,
  fetchCourseSections,
} from "../../api/overviewApi";
import { Slider, Typography, Select, MenuItem } from "@mui/material";
import dayjs from "dayjs";
import RadarChart from "./RadarChart";
import { studentAliasMap } from "../Constants.js"; // for student name aliasing
import IndividualClickBarChart from "./IndividualClickBar"; // import the bar chart component
import TrendChart from "./IndividualTrend.js";

const booleanReverse = {
  1: 'No',
  2: 'Sometimes',
  3: 'Yes',
}

const renderAnswer = (avg) => {
  if (avg == null) return "Not available";
  const val = parseInt(avg, 10);
  const label = booleanReverse[val];
  let color = "inherit";
  if (val === 2) color = "#f59e0b"; // amber
  if (val === 3) color = "#dc2626"; // red
  return <span style={{ color }}>{label}</span>;
};

const Individuals = () => {
  const [studentQuestionAverage, setStudentQuestionAverage] = useState([]);
  const [studentQuestionAverageByQuiz, setStudentQuestionAverageByQuiz] = useState([]);
  const [questionAverage, setQuestionAverage] = useState([]);
  const [error, setError] = useState(null);
  const [allDates, setAllDates] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [submissionRange, setSubmissionRange] = useState([null, null]);
  const [selectedUser, setSelectedUser] = useState("");
  const [studentClickStats, setStudentClickStats] = useState([]);
  const [studentQuizCompletion, setStudentQuizCompletion] = useState(null);
  const [studentLatestClick, setStudentLatestClick] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [sessionInfo, setSessionInfo] = useState(null); // { courseId, name }
  const [currentScores, setCurrentScores] = useState([]); // current scores by student
  const [missingAssignments, setMissingAssignments] = useState([]); // missing assignments by student
  //const [roster, setRoster] = useState([]); // course roster
  const [activeIds, setActiveIds] = useState(() => new Set()); // canvas_user_ids with submissions in window
  const [selectedSection, setSelectedSection] = useState("all");
  const [sectionNumbers, setSectionNumbers] = useState([]);

  const [labCourse, setLabCourse] = useState(null); // { lab_canvas_course_id, lab_name } | null
  const [labCurrentScores, setLabCurrentScores] = useState([]);
  const [labMissingAssignments, setLabMissingAssignments] = useState([]);



  useEffect(() => {
    const cached = localStorage.getItem("iTOOLS_sessionInfo");
    if (cached) {
      console.log("Using cached session info");
      setSessionInfo(JSON.parse(cached));
    } else {
      console.log("No cached session info found");
      setSessionInfo({ name: "Demo Course", courseId: '93298' }); // Fallback to demo
    }
  }, []);

  useEffect(() => {
      if (!sessionInfo?.courseId) return;
      fetchCourseSections(sessionInfo.courseId)
          .then((data) => setSectionNumbers(data.sections ?? []))
          .catch((err) => console.error("Error fetching course sections:", err));
  }, [sessionInfo]);

  function generateDateRanges(minDate, maxDate, numBins = 10) {
    if (!Number.isFinite(minDate) || !Number.isFinite(maxDate)) return [];
    const step = (maxDate - minDate) / numBins;
    return Array.from({ length: numBins }, (_, i) => {
      const start = new Date(minDate + i * step).toISOString();
      const end = new Date(minDate + (i + 1) * step).toISOString();
      return [start, end];
    });
  }

  // Load submission date bounds + current scores
  useEffect(() => {
    if (!sessionInfo) return;

    console.log("Fetching all submission dates...");
    fetchAllSubmissionDates(sessionInfo?.courseId)
      .then((data) => {
        const raw = Array.isArray(data?.dates) ? data.dates : [];
        const ts = raw.map((d) => Date.parse(d)).filter((n) => Number.isFinite(n));
        // if (ts.length === 0) {
        //   // cold start: no submissions yet
        //   setAllDates([]);
        //   setDateRange([null, null]);
        //   return;
        // }
        const minDate = Math.min(...ts);
        const maxDate = Math.max(...ts);
        setSubmissionRange([minDate, maxDate]);
        

        const start_fa25 = Date.parse("2026-01-20T00:00:00-07:00");
        const end_now = Date.now();
        // setDateRange([minDate, maxDate]);
        setDateRange([start_fa25, end_now]);
        setAllDates([start_fa25, end_now]);
      })
      .catch((error) => {
        console.error("Error fetching all submission dates:", error);
        setAllDates([]);
        setDateRange([null, null]);
      });

    fetchCurrentScoresByStudent(sessionInfo?.courseId)
      .then((data) => {
        console.log("Fetched current scores by student:", data.current_scores);
        setCurrentScores(data.current_scores || []);
      })
      .catch((error) => {
        console.error("Error fetching current scores by student:", error);
      });

    fetchMissingAssignmentsByStudent(sessionInfo?.courseId)
      .then((data) => {
        console.log("Fetched missing assignments by student:", data.missing_assignments);
        setMissingAssignments(data.missing_assignments || []);
      })
      .catch((error) => {
        console.error("Error fetching missing assignments by student:", error);
      });

    fetchCourseLab(sessionInfo?.courseId)
      .then(({ hasLab, lab }) => {
        if (!hasLab) return;
        setLabCourse(lab);

        fetchCurrentScoresByStudent(lab.lab_canvas_course_id)
          .then((data) => {
            console.log("Fetched current scores for lab course:", data.current_scores);
            setLabCurrentScores(data.current_scores || []);
          })
          .catch((error) => {
            console.error("Error fetching current scores for lab course:", error);
          });

        fetchMissingAssignmentsByStudent(lab.lab_canvas_course_id)
          .then((data) => {
            console.log("Fetched missing assignments for lab course:", data.missing_assignments);
            setLabMissingAssignments(data.missing_assignments || []);
          })
          .catch((error) => console.error("Error fetching missing assignments for lab course:", error));
      })
      .catch((error) => console.error("Error fetching course lab:", error));
  }, [sessionInfo]);

  // Load all data for the selected time window
  useEffect(() => {
    const isFiniteTs = (x) => Number.isFinite(x) && x > 0;
    if (!sessionInfo || !isFiniteTs(dateRange[0]) || !isFiniteTs(dateRange[1])) return;

    const [startISO, endISO] = dateRange.map((ts) => new Date(ts).toISOString());

    fetchQuestionAverageByStudent(startISO, endISO, sessionInfo?.courseId)
      .then((data) => setStudentQuestionAverage(data?.student_question_stats ?? []))
      .catch(() => setError("Failed to load student averages"));

    fetchQuestionAverageByQuizByStudent(startISO, endISO, sessionInfo?.courseId)
      .then((data) => {
        console.log("Fetched question average by quiz by student:", data.quiz_student_stats);
        setStudentQuestionAverageByQuiz(data?.quiz_student_stats ?? []);
      })
      .catch(() => setError("Failed to load student averages by quiz"));

    fetchQuestionAverage(startISO, endISO, sessionInfo?.courseId)
      .then((data) => setQuestionAverage(data.questions ?? []))
      .catch(() => setError("Failed to load class averages"));

    fetchClickSequenceByStudent(startISO, endISO, sessionInfo?.courseId)
      .then((data) => setStudentClickStats(data?.click_sequence_stats ?? []))
      .catch(() => setError("Failed to load student click sequences"));

    fetchQuizCompletionByStudent(startISO, endISO, sessionInfo?.courseId)
      .then((data) => setStudentQuizCompletion(data?.student_question_completion ?? []))
      .catch(() => setError("Failed to load student quiz completion"));

    fetchLatestClickByStudent(startISO, endISO, sessionInfo?.courseId)
      .then((data) => setStudentLatestClick(data?.latest_click_timestamps ?? []))
      .catch(() => setError("Failed to load student latest click"));
  }, [dateRange, sessionInfo]);

  // Trend series (bin by ranges across the whole timespan)
  useEffect(() => {
    if (!sessionInfo || !allDates[0] || !allDates[1]) return;

    const [minDate, maxDate] = allDates;
    const dateRanges = generateDateRanges(minDate, maxDate);
    console.log("Generated Date Ranges:", dateRanges);

    const fetchAverageByStudent = sessionInfo.courseId
      ? (start, end) => fetchQuestionAverageByStudent(start, end, sessionInfo.courseId)
      : (start, end) => fetchDemoQuestionAverageByStudent(start, end);

    Promise.all(
      dateRanges.map(([start, end]) =>
        fetchAverageByStudent(start, end).then((data) => ({
          start,
          end,
          students: data?.student_question_stats || [],
        }))
      )
    )
      .then((timeSeriesData) => {
        const trends = {};

        timeSeriesData.forEach(({ start, end, students }) => {
          const timePoint = new Date(
            (new Date(start).getTime() + new Date(end).getTime()) / 2
          ).toISOString();

          for (const student of students) {
            const construct = student.group_code;
            const avg = parseFloat(student.avg_score);
            if (!trends[construct]) trends[construct] = [];
            trends[construct].push({
              x: timePoint,
              y: avg,
              student_id: student.canvas_user_id,
            });
          }
        });

        setTrendData(trends);
      })
      .catch((error) => {
        console.error("Error fetching trend data:", error);
      });
  }, [allDates, sessionInfo]);

  // Build map of canvas_user_id -> student_name from averages payload
  // const studentNameMap = useMemo(() => {
  //   const map = {};
  //   (studentQuestionAverage || []).forEach((d) => {
  //     map[d.canvas_user_id] = d.student_name;
  //   });
  //   return map;
  // }, [studentQuestionAverage]);

  const studentNameMap = useMemo(() => {
    const map = {};
    (currentScores || []).forEach((d) => {
      map[d.canvas_user_id] = d.student_name;
    });
    return map;
  }, [currentScores]);

  // Labeled click stats
  const labeledStats = Object.entries(studentClickStats).map(([id, stat]) => {
    const realName = studentNameMap[id] ?? "Unnamed";
    const alias = studentAliasMap[realName] ?? realName;
    return {
      canvas_user_id: id,
      realName: realName,
      name: alias,
      ...stat,
    };
  });

  console.log("Individual-studentClickStats", studentClickStats);
  console.log("Individual-labeledStats:", labeledStats);
  

  const handleSliderChange = (event, newValue) => setDateRange(newValue);

  const selectedCompletion = studentQuizCompletion?.find(
    (d) => d.student_name === selectedUser
  );

  const selectedScore = currentScores?.find(
    (d) => d.student_name === selectedUser
  );

  const selectedMissing = missingAssignments?.find(
    (d) => d.student_name === selectedUser
  );

  const selectedLabScore = labCurrentScores?.find(
    (d) => d.student_name === selectedUser
  );
  const selectedLabMissing = labMissingAssignments?.find(
    (d) => d.student_name === selectedUser
  );


  // retrieve the selected student's quiz-by-quiz averages
  const selectedResByQuiz = studentQuestionAverageByQuiz?.filter(
    d => d.student_name === selectedUser
  );
  const lastWithV2 = [...selectedResByQuiz].reverse().find(d => d.group_code === 'with_v2');
  const lastSupp = [...selectedResByQuiz].reverse().find(d => d.group_code === 'supp');

  console.log("Selected student quiz-by-quiz averages:", selectedResByQuiz);
  console.log("Last with_v2 entry:", booleanReverse[parseInt(lastWithV2?.avg_score)]);
  console.log("Last supp entry:", booleanReverse[parseInt(lastSupp?.avg_score)]);

  const selectedCanvasUserId = Object.entries(studentNameMap).find(
    ([canvasUserId, name]) => name === selectedUser
  )?.[0];

  const selectedLatestClick = studentLatestClick?.find(
    (d) => String(d.canvas_user_id) === selectedCanvasUserId
  );

  const filteredTrendData = useMemo(() => {
    if (!selectedCanvasUserId) return {};
    const filtered = {};
    for (const [construct, values] of Object.entries(trendData)) {
      filtered[construct] = values.filter(
        (d) => String(d.student_id) === String(selectedCanvasUserId)
      );
    }
    return filtered;
  }, [trendData, selectedCanvasUserId]);

  const roster = useMemo(() => {
    // current_scores looks like: [{ canvas_user_id, student_name, current_score }, ...]
    return (currentScores || [])
      .map(({ canvas_user_id, student_name, current_score }) => ({
        canvas_user_id,
        student_name,
        current_score,
    }))
      .sort((a, b) => a.student_name.localeCompare(b.student_name));
      // .sort((a, b) => {
      //   if (a.current_score == null) return 1;
      //   if (b.current_score == null) return -1;
      //   return a.current_score - b.current_score;
      // });
  }, [currentScores]);

  const isDemo = !sessionInfo?.courseId || String(sessionInfo?.courseId) === '93298';

  const filteredRoster = useMemo(() => {
      let base = roster;

      if (selectedSection !== "all") {
          base = base.filter((s) => {
              const match = studentQuestionAverage.find(
                  d => String(d.canvas_user_id) === String(s.canvas_user_id)
              );
              return match?.section_number === selectedSection;
          });
      }

      return isDemo
          ? base.filter((s) => studentAliasMap[s.student_name] != null)
          : base;
  }, [roster, selectedSection, studentQuestionAverage, isDemo]);

  useEffect(() => {
      setSelectedUser("");
  }, [selectedSection]);

  // ---- NEW: compute activeIds (who has submissions in the current window) ----
  useEffect(() => {
    if (!studentQuizCompletion) {
      setActiveIds(new Set());
      return;
    }
    const ids = new Set(
      (studentQuizCompletion || []).map((r) =>
        String(r.canvas_user_id ?? r.user_id)
      )
    );
    setActiveIds(ids);
  }, [studentQuizCompletion]);

  // ---- NEW: auto-select the first active student when roster + activeIds are ready ----
  useEffect(() => {
    if (selectedUser) return;
    if (!filteredRoster?.length) return;

    const firstActive = filteredRoster.find((s) => activeIds.has(String(s.canvas_user_id)));
    if (firstActive) setSelectedUser(firstActive.student_name);
  }, [filteredRoster, activeIds, selectedUser]);

  return (
    <div className="wrapper">
      {/* <h1 className="title">INDIVIDUAL STUDENT DATA</h1> */}

      <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", flexWrap: "wrap", marginBottom: "1rem" }}>
        <h1 className="title" style={{ margin: 0 }}>INDIVIDUAL STUDENT DATA</h1>
        {sectionNumbers.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Typography variant="subtitle1"><strong>Filter by:</strong></Typography>
                <Select
                    value={selectedSection}
                    onChange={(e) => setSelectedSection(e.target.value)}
                    size="small"
                    style={{ minWidth: 160 }}
                >
                    <MenuItem value="all">All Sections</MenuItem>
                    {sectionNumbers.map((sec) => (
                        <MenuItem key={sec} value={sec}>Section {sec}</MenuItem>
                    ))}
                </Select>
            </div>
        )}
    </div>

      <div className="section-content">
        {/* Left panel: controls */}
        <div className="control-panel">
          {Number.isFinite(allDates[0]) &&
            Number.isFinite(allDates[1]) &&
            Number.isFinite(dateRange[0]) &&
            Number.isFinite(dateRange[1]) && (
              <>
                <Typography gutterBottom>
                  Select time range of survey submissions:
                </Typography>
                <div className="slider-wrapper">
                  <Slider
                    value={dateRange}
                    onChange={handleSliderChange}
                    valueLabelDisplay="auto"
                    min={allDates[0]}
                    max={allDates[1]}
                    step={24 * 60 * 60 * 1000}
                    valueLabelFormat={(ts) => dayjs(ts).format("MMM D")}
                  />
                </div>
              </>
            )}

          <Typography variant="subtitle1" gutterBottom>
            <strong>Select a student:</strong>
          </Typography>
          <Select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            style={{ width: "100%", marginBottom: "2rem" }}
          >
            {filteredRoster.map((s) => {
              const label = studentAliasMap[s.student_name] || s.student_name;
              return (
                // <MenuItem
                //   key={s.canvas_user_id}
                //   value={s.student_name}
                //   disabled={!enabled}
                //   style={!enabled ? { opacity: 0.5 } : undefined}
                // >
                //   {label}
                //   {!enabled ? " (no submissions yet)" : ""}
                // </MenuItem>
                <MenuItem
                  key={s.canvas_user_id}
                  value={s.student_name}
                >
                  {label}
                </MenuItem>
              );
            })}
          </Select>
          {/* {roster?.length > 0 && activeIds.size === 0 && (
            <Typography variant="caption" color="textSecondary">
              No submissions yet.
            </Typography>
          )} */}
        </div>

        {/* Right panel: bar chart */}
        <div className="chart-panel">
          <h2 className="section-title">1. Student Profile</h2>
          <div className="info-box">
            {/* <p><strong>Current Score: </strong>
                {selectedCompletion?.percent_answered != null
                    ? `${selectedCompletion.percent_answered}%`
                    : 'Loading...'}
            </p> */}
            <p>
              <strong>Current Grade: </strong>
              {selectedScore?.current_score != null
                ? `${selectedScore.current_score}%`
                : "Not available"}
              {labCourse && (
                <span> (Lab: {selectedLabScore?.current_score != null
                  ? `${selectedLabScore.current_score}%`
                  : "Not available"})
                </span>
              )}
            </p>
            <p>
              <strong>Number of Missing Assignments: </strong>
              {selectedMissing?.missing_assignments != null
                ? selectedMissing.missing_assignments
                : "Not available"}
              {labCourse && (
                  <span> (Lab: {selectedLabMissing?.missing_assignments != null
                    ? selectedLabMissing.missing_assignments
                    : "Not available"})
                  </span>
                )}
            </p>
            <p>
              <strong>Last Login: </strong>
              {selectedLatestClick?.latest_timestamp
                ? dayjs(selectedLatestClick.latest_timestamp).format(
                    "MM/DD/YYYY HH:mm:ss [UTC]"
                  )
                : "Not available"}
            </p>
            <p>
              <strong>I am considering withdrawing from this class: </strong>
              {renderAnswer(lastWithV2?.avg_score)}
            </p>
            <p>
              <strong>I feel like I need extra help in this class: </strong>
              {lastSupp ? booleanReverse[parseInt(lastSupp.avg_score)] : "Not available"}
            </p>
          </div>
          {/* <p>
            *The data reflects the most recently refreshed information from our
            data source. There may occasionally be a minor delay compared to the
            live course information on WebCampus.
          </p> */}
          <h2 className="section-title">2. Aggregated Survey Data</h2>

          <div className="radar-wrapper">
            {questionAverage.length > 0 ? (
              <RadarChart
                data={studentQuestionAverage}
                selectedStudent={selectedUser}
                avg={questionAverage}
              />
            ) : (
              <p className="muted-text">
                Survey data will populate once the quiz becomes available.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="section-content">
        {/* Left panel: click stats explanation */}
        <div className="control-panel">
          <p>
            <strong>Glossary:</strong>
          </p>
          <ul>
                    <li>
                        <strong>Click Coverage:</strong> Shows how much of the course the student has explored (e.g., assignments, quizzes, discussions). <u> Higher scores indicate broader exposure to course materials</u>.
                    </li>
                    <li>
                        <strong>Click Intensity:</strong> Reflects how actively the student engages with key course tools. <u>Higher scores indicate more frequent interaction with learning activities</u>.
                    </li>
                    <li>
                        <strong>Click Coherence:</strong>  Indicates whether the student tends to move through course materials in a logical, course-aligned sequence. <u> Higher scores suggest more organized and goal-directed navigation</u>.
                    </li>
                </ul>
        </div>
        {/* Right panel: click stats table */}
        <div className="chart-panel">
          <h2 className="section-title">3. WebCampus Click Data</h2>
          <p>
            Standardized scores are shown below to indicate the activity
            patterns in comparison with their classmates. Class average is set
            to 0. A negative score means it's below the class average and a
            positive value means above it.
          </p>
          <div className="chart-wrapper">
            <IndividualClickBarChart
              labeledStats={labeledStats}
              selectedId={selectedUser}
            />
          </div>
        </div>
      </div>

      <div className="section-content">
        <div className="chart-panel">
          <h2 className="section-title">4. Survey Data Trends</h2>
          <p>
            The charts below show the selected student's changes in each
            measure. You can also use this chart to check the number of data
            points collected for the student and identify any excessive missing
            data. The numbers on the x-axis represent the submitted date of the
            surveys. You can click on each measure's legend to mute or unmute
            it. Hover over a data point to see the exact score.
          </p>
          {questionAverage.length > 0 ? (
            <TrendChart trendData={filteredTrendData} />
          ) : (
            <p className="muted-text">Survey data will populate once the quiz becomes available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Individuals;
